import React, { useCallback, forwardRef, FC, useRef } from 'react';

import { Address } from 'ia-react-core/~/interfaces/Address';
import excludePropsByComponent from '../../../utils/components/_excludePropsByComponent';
import Control, { ControlProps, ControlRenderProps } from '../Control';
import AddressSuggestInput, { AddressSuggestInputProps, PostCanadaFindItem } from '../AddressSuggestInput';

export type AddressSuggestInputControlValidationProps = {
  /** Adds the `required` validator to the control. The control will be invalid if the value is empty. */
  required?: boolean;
  /** Adds the `recommended` validator to the control. The control will display a warning if the value is empty. */
  recommended?: boolean;
};

export interface AddressSuggestControlProps extends
    Omit<AddressSuggestInputProps, 'defaultValue' | 'name' | 'value' | 'children' | 'id' | 'isTouched' | 'isValid' | 'onKeyDown' | 'onTouched' | 'onValueChange'>,
    Omit<ControlProps<Address | PostCanadaFindItem | string, Address | string, HTMLInputElement, AddressSuggestInputControlValidationProps>, 'children' | 'id'>,
    AddressSuggestInputControlValidationProps {
}

interface AddressSuggestInputControlInternalProps extends Omit<ControlRenderProps<Address | PostCanadaFindItem, HTMLInputElement>, 'onBlur' | 'onChange' | 'onFocus'> {
  onValueChangeCallback: ControlRenderProps<Address | PostCanadaFindItem | '', HTMLInputElement>['onValueChange'];
  onTouchedCallback: ControlRenderProps<Address | PostCanadaFindItem | '', HTMLInputElement>['onTouched'];
}

export interface AddressSuggestInputControlComposedProps extends AddressSuggestControlProps, AddressSuggestInputControlInternalProps {

}

export const AddressSuggestControlDefaultProps = {};

const AddressSuggestControl = Object.assign(
  forwardRef<HTMLInputElement, AddressSuggestInputControlComposedProps>(({
    id, name, required, defaultValue, readOnly, textOnly, value, isValid, isTouched, errorMessagesId, onValueChange, onTouched, onSearch, onValueChangeCallback, onTouchedCallback, ...props
  }, ref) => {
    const latestValueRef = useRef<Address | PostCanadaFindItem | string>(value);
    const handleValueChange = useCallback((newValue: Address | PostCanadaFindItem | string) => {
      latestValueRef.current = newValue;
      onValueChangeCallback(newValue as Address | PostCanadaFindItem);
      if (onValueChange) onValueChange(newValue as Address | PostCanadaFindItem);
    }, [onValueChange, onValueChangeCallback]);

    const handleTouched = useCallback(() => {
      setTimeout(() => {
        const newValue = latestValueRef.current;
        onTouchedCallback(newValue as Address | PostCanadaFindItem, { shouldCommit: false });
        if (onTouched) onTouched(newValue as Address | PostCanadaFindItem);
      }, 200);
    }, [onTouched, onTouchedCallback]);

    return (
      <AddressSuggestInput
        id={id}
        data-control
        data-testid="AddressSuggestControl"
        aria-invalid={Boolean(!isValid && isTouched)}
        aria-errormessage={errorMessagesId}
        readOnly={readOnly}
        textOnly={textOnly}
        {...excludePropsByComponent(props, Control)}
        required={required}
        name={name}
        value={value as Address}
        isValid={isValid}
        isTouched={isTouched}
        ref={ref}
        onValueChange={handleValueChange}
        onTouched={handleTouched}
      />
    );
  }) as FC<AddressSuggestInputControlComposedProps>,
  {
    labeledControl: true,
    defaultProps: AddressSuggestControlDefaultProps,
  },
);

export default AddressSuggestControl;
